import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import type { TableMatrixPayloadEndpoint, TableMatrixPayloadTemplate } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import type { PayloadTemplateDraft, PayloadTemplateEndpointRow, PayloadTemplateKvRow } from './dvm-layout-builder.types';

/** What the shell hands the modal when a "Body" button is clicked. */
export interface PayloadTemplateModalRequest {
  /** CRUD endpoint key (a Format control key such as `saveUrl`). */
  endpointKey: string;
  endpointLabel: string;
  /** The template saved for that endpoint so far, if any. */
  template: TableMatrixPayloadTemplate | undefined;
  /** Columns offered under "Columns to send". */
  columns: string[];
}

/** The `payloadTemplates` map of a Format record. */
export function payloadTemplatesOn(format: Record<string, unknown> | undefined): Record<string, TableMatrixPayloadTemplate> {
  return (format?.['payloadTemplates'] as Record<string, TableMatrixPayloadTemplate> | undefined) || {};
}

export function hasPayloadTemplate(format: Record<string, unknown> | undefined, endpointKey: string): boolean {
  return !!payloadTemplatesOn(format)[endpointKey];
}

/**
 * "Custom request body" popup — scoped to ONE CRUD endpoint of a table tile
 * (whichever "Body" button opened it). Row edits (the table's own columns)
 * are the DYNAMIC part and need no editor: the chart component sends them
 * automatically. This popup only edits the STATIC scaffolding: constants
 * merged alongside row data, the endpoints array, and extra top-level
 * fields. `save` emits the resulting template (undefined when everything is
 * empty, which removes the endpoint's template).
 */
@Component({
  selector: 'app-payload-template-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule],
  templateUrl: './payload-template-modal.component.html',
  styles: [':host { display: contents; }'],
})
export class PayloadTemplateModalComponent implements OnChanges {
  @Input({ required: true }) request!: PayloadTemplateModalRequest;
  @Output() readonly save = new EventEmitter<TableMatrixPayloadTemplate | undefined>();
  @Output() readonly cancel = new EventEmitter<void>();

  draft: PayloadTemplateDraft = emptyDraft();
  availableColumns: string[] = [];

  get endpointLabel(): string {
    return this.request?.endpointLabel ?? '';
  }

  ngOnChanges(): void {
    if (!this.request) return;
    this.draft = draftFrom(this.request.template);
    const seen = new Set<string>();
    this.availableColumns = [...this.request.columns, ...this.draft.includeColumns]
      .map((column) => String(column ?? '').trim())
      .filter((column) => column && !seen.has(column) && (seen.add(column), true));
  }

  isColumnSelected(column: string): boolean {
    return this.draft.includeColumns.includes(column);
  }
  toggleColumn(column: string): void {
    const list = this.draft.includeColumns;
    const at = list.indexOf(column);
    if (at >= 0) list.splice(at, 1);
    else list.push(column);
  }
  clearColumns(): void { this.draft.includeColumns = []; }

  addKvRow(rows: PayloadTemplateKvRow[]): void { rows.push({ key: '', value: '' }); }
  removeKvRow(rows: PayloadTemplateKvRow[], index: number): void { rows.splice(index, 1); }
  addEndpointRow(rows: PayloadTemplateEndpointRow[]): void { rows.push({ functionName: '', version: '' }); }
  removeEndpointRow(rows: PayloadTemplateEndpointRow[], index: number): void { rows.splice(index, 1); }

  onSave(): void {
    this.save.emit(templateFromDraft(this.draft));
  }

  onCancel(): void {
    this.cancel.emit();
  }
}

// ── draft ↔ template (pure) ─────────────────────────────────────────────────

function emptyDraft(): PayloadTemplateDraft {
  return { dataKey: 'requestData', includeColumns: [], staticValues: [], endpoints: [], extraFields: [] };
}

function kvRowsFrom(record?: Record<string, unknown>): PayloadTemplateKvRow[] {
  return record ? Object.keys(record).map((key) => ({
    key,
    value: record[key] !== null && typeof record[key] === 'object'
      ? JSON.stringify(record[key])
      : String(record[key] ?? ''),
  })) : [];
}

function endpointRowsFrom(list?: TableMatrixPayloadEndpoint[]): PayloadTemplateEndpointRow[] {
  return (list || []).map((e) => ({ functionName: e.functionName || '', version: e.version || '' }));
}

function draftFrom(tpl: TableMatrixPayloadTemplate | undefined): PayloadTemplateDraft {
  const t = tpl || {};
  return {
    dataKey: t.dataKey || 'requestData',
    includeColumns: [...(t.includeColumns || [])],
    staticValues: kvRowsFrom(t.staticValues),
    endpoints: endpointRowsFrom(t.endpoints),
    extraFields: kvRowsFrom(t.extraFields),
  };
}

/** Key/value rows → record; a value that parses as JSON is kept as JSON, anything else as text. */
function recordFrom(rows: PayloadTemplateKvRow[]): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  rows.forEach((r) => {
    const k = r.key.trim();
    if (!k) return;
    const value = r.value.trim();
    if (!value) { out[k] = ''; return; }
    try {
      out[k] = JSON.parse(value);
    } catch {
      out[k] = r.value;
    }
  });
  return Object.keys(out).length ? out : undefined;
}

function endpointsFrom(rows: PayloadTemplateEndpointRow[]): TableMatrixPayloadEndpoint[] | undefined {
  const out = rows.filter((r) => r.functionName.trim()).map((r) => ({ functionName: r.functionName.trim(), version: r.version.trim() || undefined }));
  return out.length ? out : undefined;
}

function templateFromDraft(d: PayloadTemplateDraft): TableMatrixPayloadTemplate | undefined {
  const staticValues = recordFrom(d.staticValues);
  const endpoints = endpointsFrom(d.endpoints);
  const extraFields = recordFrom(d.extraFields);
  const includeColumns = d.includeColumns.length ? [...d.includeColumns] : undefined;
  if (!staticValues && !endpoints && !extraFields && !includeColumns) return undefined;
  return { dataKey: d.dataKey.trim() || undefined, includeColumns, staticValues, endpoints, extraFields };
}
